import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import {
  Business as BusinessIcon,
  ManageAccounts as ManageAccountsIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import { fetchOperators } from '../../redux/slices/operatorsSlice';
import {
  isCentralRole,
  isOperatorRole,
  selectCanManageOperators,
  selectCurrentUser
} from '../../redux/slices/authSlice';

const getRoleLabel = (role) => {
  switch (role) {
    case 'admin':
      return 'Administrator';
    case 'central_admin':
      return 'Zentrale';
    case 'central_dispatch':
      return 'Zentrale Disposition';
    case 'dispatcher':
      return 'Dispatcher';
    case 'operator_admin':
      return 'Unternehmer-Admin';
    case 'operator_staff':
      return 'Unternehmer-Mitarbeiter';
    default:
      return role || 'Unbekannt';
  }
};

const getUserDisplayName = (user) => (
  user?.displayName || user?.name || user?.email || 'Benutzer'
);

const getUserInitial = (user) => {
  const displayName = getUserDisplayName(user).trim();
  return displayName ? displayName.charAt(0).toUpperCase() : 'U';
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const canManageOperators = useSelector(selectCanManageOperators);
  const { operators } = useSelector(state => state.operators);

  useEffect(() => {
    if (user && (isCentralRole(user) || isOperatorRole(user))) {
      dispatch(fetchOperators());
    }
  }, [dispatch, user]);

  const ownOperator = user?.operatorId
    ? operators.find(operator => operator.id === user.operatorId)
    : null;

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Profil"
        description="Kontodaten, Rolle und Zugriffsverwaltung"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Profil', path: '/profil' }
        ]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  src={user?.photoURL || ''}
                  alt={getUserDisplayName(user)}
                  sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}
                >
                  {getUserInitial(user)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {getUserDisplayName(user)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email || 'Keine E-Mail'}
                  </Typography>
                  <Chip
                    size="small"
                    label={getRoleLabel(user?.role)}
                    color={isCentralRole(user) ? 'primary' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <List dense>
                <ListItem disableGutters>
                  <ListItemText primary="Benutzer-ID" secondary={user?.uid || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Organisation" secondary={user?.organizationId || 'Standard'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Unternehmer" secondary={ownOperator?.name || user?.operatorId || 'Nicht zugeordnet'} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <ManageAccountsIcon color="primary" />
                <Typography variant="h6">Zugriff & Sub-Konten</Typography>
              </Stack>

              {canManageOperators ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Zentrale Admins verwalten hier Unternehmer, Fahrzeugkontingente und Portal-Sub-Konten.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      component={RouterLink}
                      to="/profil/unternehmer"
                      startIcon={<BusinessIcon />}
                    >
                      Unternehmer verwalten
                    </Button>
                    <Button
                      variant="outlined"
                      component={RouterLink}
                      to="/profil/sub-konten"
                      startIcon={<PersonIcon />}
                    >
                      Sub-Konten verwalten
                    </Button>
                  </Stack>
                </>
              ) : isOperatorRole(user) ? (
                <Alert severity="info">
                  Ihr Konto ist einem Unternehmer zugeordnet. Die Verwaltung weiterer Sub-Konten ist derzeit der Zentrale vorbehalten.
                </Alert>
              ) : (
                <Alert severity="info">
                  Fuer dieses Konto sind keine erweiterten Zugriffsverwaltungsfunktionen freigeschaltet.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
};

export default ProfilePage;
